import { html, type Html, View } from 'rune-ts';
import { MpsScrollSyncItem, MpsScrollSyncOptions, MpsScrollSyncPreset } from './types';
import klass from './mps-scroll-sync.module.scss';
import { AnimationConfig, MpsAnimationController } from './util/mps-animation-controller.ts';
import { DragCallbacks, MpsDragController } from './util/mps-drag-controller.ts';

/* 준만님께서 마플에서 사용하려고 만드신 ScrollSync 를 포크(복사)떠서 사용 */

export interface MpsScrollSyncParams {
  lists: MpsScrollSyncItem[];
  option?: MpsScrollSyncOptions;
  preset?: MpsScrollSyncPreset;
  style?: {
    container?: string;
    list?: string;
  };
}

// ===========================================
// 상수 정의
// ===========================================

const DEFAULT_SETTINGS = {
  wheelSensitivity: 0.1,
  dragSensitivity: 1.3,
  animationSpeed: 0.1,
} as const;

const ANIMATION_CONFIG: AnimationConfig = {
  threshold: 0.01, // 10배 빠른 종료 (10ms)
  dragSpeedMultiplier: 2, // 덜 빠른 반응
  friction: 0.9, // 빠른 감속
  minVelocity: 0.002, // 빠른 정지
  minDragInertia: 0.3, // 드래그 발생 시 최소 보장 속도
};

// ===========================================
// 메인 클래스
// ===========================================

export class MpsScrollSync extends View<MpsScrollSyncParams> {
  // 설정 및 상태
  private scrollSettings: MpsScrollSyncOptions = { ...DEFAULT_SETTINGS };
  private progressState = {
    current: 0,
    target: 0,
  };

  // DOM 요소들
  private maxScrolls: number[] = [];
  private lists: HTMLElement[] = [];

  private dragController: MpsDragController;
  private animationController: MpsAnimationController;

  constructor(params: MpsScrollSyncParams) {
    super(params);
    this.applySettings();
    this.dragController = new MpsDragController(
      {
        dragSensitivity: this.scrollSettings.dragSensitivity,
      },
      this.createDragCallbacks(),
    );

    this.animationController = new MpsAnimationController(ANIMATION_CONFIG, (progress) => {
      this.progressState.current = progress;
      this.applyTransform();
    });
  }

  // ===========================================
  // 렌더링
  // ===========================================

  protected override template(): Html {
    const { lists, style } = this.data;
    return html`
      <div class="${klass.scrollContainer} ${style?.container ?? ''}">
        <div class="${klass.listsContainer} ${style?.list ?? ''}">${lists}</div>
      </div>
    `;
  }

  protected override onRender() {
    setTimeout(() => this.initialize(), 0);
  }

  // ===========================================
  // 드래그 콜백
  // ===========================================
  private createDragCallbacks(): DragCallbacks {
    return {
      onDragStart: () => {
        this.animationController.stop();
      },
      onDragMove: (progress) => {
        this.progressState.target = Math.max(0, Math.min(1, progress));
        this.animationController.startDragAnimation(this.progressState, this.scrollSettings.animationSpeed);
      },
      onDragEnd: (velocity) => {
        this.handleDragEnd(velocity);
      },
      onClickableElement: (element: HTMLElement) => this.isClickableElement(element),
    };
  }

  private handleDragEnd(velocity: number) {
    // 최소 속도 보장 및 부스트 적용
    const absVelocity = Math.abs(velocity);
    const velocityDirection = Math.sign(velocity);

    if (absVelocity > 0) {
      const adjustedMinInertia = ANIMATION_CONFIG.minDragInertia * this.scrollSettings.dragSensitivity;

      if (Math.abs(velocity) < adjustedMinInertia) {
        velocity = adjustedMinInertia * velocityDirection;
      }
    } else {
      // 속도가 0인 경우 최소 속도 적용
      const dragState = this.dragController.getState();
      const dragDirection = dragState.lastX > dragState.startX ? 1 : -1;
      velocity = ANIMATION_CONFIG.minDragInertia * this.scrollSettings.dragSensitivity * dragDirection;
    }

    this.animationController.startInertia(this.progressState, velocity, this.element().clientWidth);
  }

  // ===========================================
  // 초기화
  // ===========================================

  private applySettings() {
    if (this.data.preset) {
      this.applyPreset(this.data.preset);
    }
    if (this.data.option) {
      this.updateOptions(this.data.option);
    }
  }

  private updateOptions(option: MpsScrollSyncOptions) {
    if (option.dragSensitivity !== undefined) this.setDragSensitivity(option.dragSensitivity);
    if (option.wheelSensitivity !== undefined) this.setWheelSensitivity(option.wheelSensitivity);
    if (option.animationSpeed !== undefined) this.setAnimationSpeed(option.animationSpeed);
  }

  protected initialize() {
    const scrollContainer = this.element();
    this.calculateScrollLimits(scrollContainer);
    this.setupEventListeners(scrollContainer);
  }

  private calculateScrollLimits(scrollContainer: HTMLElement) {
    const containerWidth = scrollContainer.clientWidth;
    const padding = 16;

    const listsContainer = scrollContainer.querySelector(`.${klass.listsContainer}`);
    const listsElements = Array.from(listsContainer?.children || []) as HTMLElement[];
    const listsWidths = listsElements.map((el) => el.scrollWidth);

    this.maxScrolls = listsWidths.map((width) => Math.max(0, width - containerWidth + padding));
    this.lists = listsElements;
  }

  // ===========================================
  // 이벤트 처리
  // ===========================================

  private setupEventListeners(scrollContainer: HTMLElement) {
    this.addDragEvents(scrollContainer);
    this.addWheelEvents(scrollContainer);
    this.setInitialStyles(scrollContainer);
  }

  private addDragEvents(scrollContainer: HTMLElement) {
    const handleStart = this.dragController.createStartHandler();
    const handleMove = this.dragController.createMoveHandler();
    const handleEnd = this.dragController.createEndHandler();

    // 마우스 이벤트
    scrollContainer.addEventListener('mousedown', (e) => {
      this.dragController.setStartProgress(this.progressState.current);
      handleStart(e);
    });
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleEnd);

    // 터치 이벤트
    scrollContainer.addEventListener(
      'touchstart',
      (e) => {
        this.dragController.setStartProgress(this.progressState.current);
        handleStart(e);
      },
      { passive: false },
    );
    document.addEventListener('touchmove', handleMove, { passive: false });
    document.addEventListener('touchend', handleEnd);
  }

  private addWheelEvents(scrollContainer: HTMLElement) {
    scrollContainer.addEventListener('wheel', (e: WheelEvent) => {
      e.stopPropagation();
      e.preventDefault();

      const delta = e.deltaX || e.deltaY;
      const newProgress =
        this.progressState.target +
        (delta > 0 ? this.scrollSettings.wheelSensitivity : -this.scrollSettings.wheelSensitivity);

      this.updateScroll(newProgress);
    });
  }

  private setInitialStyles(scrollContainer: HTMLElement) {
    scrollContainer.style.userSelect = 'none';
    scrollContainer.style.touchAction = 'pan-y';
  }

  private isClickableElement(element: HTMLElement): boolean {
    const clickableTags = ['BUTTON', 'A', 'INPUT', 'SELECT', 'TEXTAREA', 'LABEL'];
    const clickableRoles = ['button', 'link', 'menuitem', 'tab'];

    // 태그명 확인
    if (clickableTags.includes(element.tagName)) {
      return true;
    }

    // role 속성 확인
    const role = element.getAttribute('role');
    if (role && clickableRoles.includes(role)) {
      return true;
    }

    // 클릭 이벤트 리스너가 있는 요소 확인
    if (element.onclick || element.getAttribute('onclick')) {
      return true;
    }

    // 부모 요소도 확인 (최대 3단계까지)
    let parent = element.parentElement;
    let depth = 0;
    while (parent && depth < 3) {
      const parentRole = parent.getAttribute('role');
      if (
        clickableTags.includes(parent.tagName) ||
        (parentRole && clickableRoles.includes(parentRole)) ||
        parent.onclick ||
        parent.getAttribute('onclick')
      ) {
        return true;
      }
      parent = parent.parentElement;
      depth++;
    }

    return false;
  }

  // ===========================================
  // 애니메이션 시스템
  // ===========================================

  private updateScroll(progress: number) {
    this.progressState.target = Math.max(0, Math.min(1, progress));
    this.animationController.startAnimation(this.progressState, this.scrollSettings.animationSpeed);
  }

  private applyTransform() {
    this.maxScrolls.forEach((maxScroll, index) => {
      const list = this.lists[index];
      if (list) {
        const offset = this.progressState.current * maxScroll;
        list.style.transform = `translateX(-${offset}px)`;
      }
    });
  }

  // ===========================================
  // 설정 관리
  // ===========================================

  private setWheelSensitivity(value: number) {
    this.scrollSettings.wheelSensitivity = Math.max(0.01, Math.min(0.2, value));
  }

  private setDragSensitivity(value: number) {
    this.scrollSettings.dragSensitivity = Math.max(0.1, Math.min(3, value));
  }

  private setAnimationSpeed(value: number) {
    this.scrollSettings.animationSpeed = Math.max(0.01, Math.min(1, value));
  }

  private applyPreset(preset: MpsScrollSyncPreset) {
    const presets = {
      slow: {
        wheelSensitivity: 0.02,
        dragSensitivity: 0.3,
        animationSpeed: 0.05,
      },
      middle: {
        wheelSensitivity: 0.1,
        dragSensitivity: 1.9,
        animationSpeed: 1,
      },
      fast: {
        wheelSensitivity: 0.1,
        dragSensitivity: 2.5,
        animationSpeed: 1,
      },
      default: DEFAULT_SETTINGS,
    };

    const settings = presets[preset] || presets.default;
    this.setWheelSensitivity(settings.wheelSensitivity);
    this.setDragSensitivity(settings.dragSensitivity);
    this.setAnimationSpeed(settings.animationSpeed);
  }
}
